import vicmapAerialIcon from "../assets/images/vicmap-aerial-icon.png";
import vicmapCartoIcon from "../assets/images/vicmap-carto-icon.png";
import colours from "./colours";
import { SearchControlPlaceholder } from "./constants";
import { basemapTypes, treeSize, treeType } from "./types";

const mapshareAddressGeocoder =
  "https://corp-geo.mapshare.vic.gov.au/arcgis/rest/services/Geocoder/VMAddressEZIAdd/GeocodeServer";
const mapshareParcelGeocoder =
  "https://corp-geo.mapshare.vic.gov.au/arcgis/rest/services/Geocoder/VMParcelSPI/GeocodeServer";
export const arcgisServerBaseUrl =
  "https://nvr-gis.mapshare.vic.gov.au/arcgis/rest/services";
export const geometryServiceUrl =
  arcgisServerBaseUrl + "/Utilities/Geometry/GeometryServer";

export const mapImageLayerName = "nvr";

const mapConfig: any = {
  initialExtent: {
    xmin: 2429801,
    ymin: 2366989,
    xmax: 2568497,
    ymax: 2457491,
  },
  sr: 7899,
  minScale: 10000000,
  maxScale: 10,
  minDrawingScale: 50000,
  arcgisServerBaseUrl: arcgisServerBaseUrl,
  arcgisServerGeometryUrl: `${arcgisServerBaseUrl}/Utilities/Geometry/GeometryServer`,
  basemaps: {
    vicmapCarto: {
      title: "Vicmap - Carto",
      type: basemapTypes.wmts,
      url: "https://base.maps.vic.gov.au/service",
      thumbnail: vicmapCartoIcon,
      layerId: "CARTO_VG2020",
      serviceMode: "KVP",
      opacity: 1, // Opacity must be 1 otherwise WMTS basemap covers all operational layers when viewed on mobile device.
      maxScale: 1, // This is set at 1 as otherwise basemap gallery has an issue thinking vicmapAerial is the same layer if they both have the same props
    },
    vicmapAerial: {
      title: "Vicmap - Aerial",
      type: basemapTypes.wmts,
      url: "https://base.maps.vic.gov.au/service",
      thumbnail: vicmapAerialIcon,
      layerId: "AERIAL_VG2020",
      serviceMode: "KVP",
      opacity: 1, // Opacity must be 1 otherwise WMTS basemap covers all operational layers when viewed on mobile device.
      maxScale: 0,
    },
  },
  mapServiceConfig: {
    url: `${arcgisServerBaseUrl}/NVR2/NVR/MapServer`,
    visibleLayers: [24, 10], // wetlands, msa levy area
    // The below group layers will be shown as single legend items, ignoring their sublayers.
    // This is because their sublayers are copies of the same layer, customised for and activating at different scales.
    singleItemGroupLayers: [1, 5], // Contours, watercourses,
    layersWithOpacityControl: [24, 13, 14, 26, 27, 28, 29, 30],
    defaultOpacities: {
      24: 1,
      13: 0.85,
      14: 0.85,
      18: 0.85,
      25: 0.85,
      26: 0.85,
      27: 0.85,
      28: 0.85,
      29: 0.85,
    },
    legendItemOrder: {
      // not specified items will be ordered according to their layerId
      // these are orderings within their legend group so they don't have to be unique across groups
      27: 1, // location risk
      28: 2, // condition score
      29: 3, // strategic biod/ty value score
      25: 4, // 2005 EVC classes
      26: 5, // 1750 EVC classes
      24: 6, // wetlands last
    },
    legendGroups: [
      "Native Vegetation Regulations",
      "Landscape",
      "Melbourne Strategic Assessment",
      "Administrative",
    ],
    identifiableLayers: [24, 9, 11, 13, 14, 16, 17, 18, 19, 20, 21, 22, 25, 26],
    evcLayerId: 26,
    wetlandsLayerId: 24,
    evcLayerCodeField: "VEG_CODE",
    msaLevyAreaLayerId: 10,
    msa12PrecinctsLayerId: 11,
    msaConservationLayerId: 9,
    msaParcelLayerId: 16,
    nvrExtentLayerId: 30,
    tables: {
      evcLargeTreeBenchmark: {
        name: "NVRM_LARGE_TREE_BM",
        layerId: 31,
        codeField: "VEG_CODE",
        woodyField: "WOODY",
        speciesField: "SPECIES_TEXT",
        benchmarkField: "LTBM_NUM",
      },
    },
  },
  popupConfig: {
    24: {
      title: "Feature results",
      content: [
        {
          type: "fields",
          fieldInfos: [
            {
              fieldName: "WETLAND_NO",
              label: "Mapped Wetland ID",
            },
          ],
        },
      ],
    },
    9: {
      title: "Feature results",
      content: [
        {
          type: "fields",
          fieldInfos: [
            {
              fieldName: "CA_NAME",
              label: "Conservation Area Name",
            },
            {
              fieldName: "CA_LABEL",
              label: "Conservation Area Number",
            },
          ],
        },
      ],
    },
    11: {
      title: "Feature results",
      content: [
        {
          type: "fields",
          fieldInfos: [
            {
              fieldName: "PSP_NAME",
              label: "Precinct Structure Plan",
            },
          ],
        },
      ],
    },
    13: {
      title: "Feature results",
      content: [
        {
          type: "fields",
          fieldInfos: [
            {
              fieldName: "ZONE_DESCRIPTION",
              label: "Planning Overlay(s)",
            },
          ],
        },
      ],
    },
    14: {
      title: "Feature results",
      content: [
        {
          type: "fields",
          fieldInfos: [
            {
              fieldName: "ZONE_DESCRIPTION",
              label: "Planning Zone",
            },
          ],
        },
      ],
    },
    16: {
      title: "Feature results",
      content: [
        {
          type: "fields",
          fieldInfos: [
            {
              fieldName: "PARCEL_SPI",
              label: "Standard Parcel Identifier (SPI)",
            },
          ],
        },
      ],
    },
    17: {
      title: "Feature results",
      content: [
        {
          type: "fields",
          fieldInfos: [
            {
              fieldName: "LABEL",
              label: "Name",
            },
          ],
        },
      ],
    },
    18: {
      title: "Feature results",
      content: [
        {
          type: "fields",
          fieldInfos: [
            {
              fieldName: "BIOREGION",
              label: "Bioregion",
            },
          ],
        },
      ],
    },
    19: {
      title: "Feature results",
      content: [
        {
          type: "fields",
          fieldInfos: [
            {
              fieldName: "NAME",
              label: "RAP",
            },
          ],
        },
      ],
    },
    20: {
      title: "Feature results",
      content: [
        {
          type: "fields",
          fieldInfos: [
            {
              fieldName: "DELWP_REGION",
              label: "Region",
            },
          ],
        },
      ],
    },
    21: {
      title: "Feature results",
      content: [
        {
          type: "fields",
          fieldInfos: [
            {
              fieldName: "CMANAME",
              label: "CMA",
            },
          ],
        },
      ],
    },
    22: {
      title: "Feature results",
      content: [
        {
          type: "fields",
          fieldInfos: [
            {
              fieldName: "LGA_NAME",
              label: "LGA",
            },
          ],
        },
      ],
    },
    25: {
      title: "Feature results",
      content: [
        {
          type: "fields",
          fieldInfos: [
            {
              fieldName: "X_EVCNAME",
              label: "EVC Name",
            },
            {
              fieldName: "EVC",
              label: "EVC Code",
            },
            {
              fieldName: "BIOREGION",
              label: "Bioregion",
            },
            {
              fieldName: "EVC_BCS_DESC",
              label: "Bioregional Conservation Status",
            },
          ],
        },
      ],
    },
    26: {
      title: "Feature results",
      content: [
        {
          type: "fields",
          fieldInfos: [
            {
              fieldName: "X_EVCNAME",
              label: "EVC Name",
            },
            {
              fieldName: "EVC",
              label: "EVC Code",
            },
            {
              fieldName: "BIOREGION",
              label: "Bioregion",
            },
            {
              fieldName: "EVC_BCS_DESC",
              label: "Bioregional Conservation Status",
            },
          ],
        },
      ],
    },
  },
  searchConfig: {
    allPlaceholder: SearchControlPlaceholder,
    includeDefaultSources: false,
    locationEnabled: false,
  },
  searchSources: [
    {
      url: mapshareAddressGeocoder,
      name: "Street Address",
      minSuggestCharacters: 5,
      popupEnabled: false,
    },
    {
      url: mapshareParcelGeocoder,
      name: "Parcel SPI",
      minSuggestCharacters: 3,
      popupEnabled: false,
    },
  ],
  searchSymbolProperties: {
    style: "circle",
    outline: { color: colours.th1 },
    color: colours.th2,
    size: 12,
  },
  drawSymbols: {
    [treeType.Patch]: {
      type: "simple-marker",
      style: "circle",
      outline: { color: colours.white, width: 1 },
      color: colours[treeType.Patch],
      size: 9,
    },
    [treeType.Scattered]: {
      type: "simple-marker",
      style: "circle",
      outline: { color: colours.white, width: 1 },
      color: colours[treeType.Scattered],
      size: 9,
    },
    flaggedTree: {
      type: "simple-marker",
      style: "circle",
      outline: { color: colours.black, width: 2 },
      color: colours.flag,
      size: 9,
    },
    patch: {
      type: "simple-fill",
      color: colours.patchFill,
      outline: {
        color: colours.patchOutline,
        width: 3,
      },
    },
    editablePatch: {
      type: "simple-fill",
      color: colours.patchFill,
      outline: {
        color: colours.patchOutline,
        width: 3,
        style: "long-dash",
      },
    },
    revegetationArea: {
      type: "simple-fill",
      color: colours.revegetationAreaFill,
      outline: {
        color: colours.revegetationAreaOutline,
        width: 3,
      },
    },
    editableRevegetationArea: {
      type: "simple-fill",
      color: colours.revegetationAreaFill,
      outline: {
        color: colours.revegetationAreaOutline,
        width: 3,
        style: "long-dash",
      },
    },
    scatteredTreeBuffer: {
      type: "simple-fill",
      color: colours.scatteredBufferFill,
      outline: {
        color: colours.scatteredBufferOutline,
        width: 3,
      },
    },
    flaggedTreeBuffer: {
      type: "simple-fill",
      color: colours.flagl1,
      outline: {
        color: colours.black,
        width: 2,
      },
    },
    polygonLabel: {
      type: "text",
      color: colours.th1,
      haloColor: colours.white,
      haloSize: "1px",
      xoffset: 0,
      yoffset: -2,
      font: {
        size: 11,
        family: "Noto Sans",
        weight: "bold",
      },
    },
    treeLabelDefault: {
      type: "text",
      haloColor: colours.white,
      haloSize: "2px",
      xoffset: -3,
      yoffset: 7,
      font: {
        size: 11,
        family: "Noto Sans",
        weight: "bold",
      },
    },
  },
  treeSizes: {
    [treeSize.Lg]: 9,
    [treeSize.Sm]: 7,
  },
  treeBuffers: {
    [treeSize.Lg]: 15,
    [treeSize.Sm]: 10,
  },
};

export default mapConfig;
